import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Description,
  ExpandMore,
  CheckCircle,
  Pending,
  Cancel,
  Security,
  Info,
  Schedule,
  Person,
  Close,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';

interface DocumentProcessingHistoryProps {
  open: boolean;
  onClose: () => void;
}

interface DocumentHistoryItem {
  document_name: string;
  document_type: string;
  processing_timestamp: string;
  created_by: number;
  creator: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  };
  rules: Array<{
    id: number;
    name: string;
    approval_status: 'pending' | 'approved' | 'rejected';
    is_active: boolean;
    created_at: string;
  }>;
  statistics: {
    total_rules: number;
    approved_rules: number;
    pending_rules: number;
    active_rules: number;
  };
}

const DocumentProcessingHistory: React.FC<DocumentProcessingHistoryProps> = ({ open, onClose }) => {
  const [history, setHistory] = useState<DocumentHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRules, setSelectedRules] = useState<number[]>([]);
  const [batchApproving, setBatchApproving] = useState(false);

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get('/api/documents/processing-history');
      setHistory(response.data.processing_history || []);
    } catch (error: any) {
      console.error('Failed to fetch processing history:', error);
      setError('Failed to load document processing history');
      toast.error('Failed to load processing history');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchApprove = async () => {
    if (selectedRules.length === 0) {
      toast.error('Please select rules to approve');
      return;
    }

    setBatchApproving(true);

    try {
      const response = await axios.post('/api/documents/batch-approve', {
        rule_ids: selectedRules,
        activate: true
      });

      toast.success(response.data.message);
      setSelectedRules([]);
      fetchHistory(); // Refresh the history
    } catch (error: any) {
      console.error('Batch approval failed:', error);
      toast.error('Failed to approve rules');
    } finally {
      setBatchApproving(false);
    }
  };

  const toggleRuleSelection = (ruleId: number) => {
    setSelectedRules(prev => 
      prev.includes(ruleId) 
        ? prev.filter(id => id !== ruleId)
        : [...prev, ruleId]
    );
  };

  const getDocumentTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      'hr_policy': 'HR Policy',
      'nist': 'NIST Framework',
      'fedramp': 'FedRAMP',
      'security_policy': 'Security Policy',
      'compliance': 'Compliance Document',
      'general_policy': 'General Policy'
    };
    return types[type] || type;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingRules = history.flatMap(doc => 
    doc.rules.filter(rule => rule.approval_status === 'pending')
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Description />
            Document Processing History
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Batch Actions */}
            {pendingRules.length > 0 && (
              <Card sx={{ mb: 3, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Pending Approvals
                      </Typography>
                      <Typography variant="body2">
                        {pendingRules.length} rules from document processing are waiting for approval
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Typography variant="caption">
                        {selectedRules.length} selected
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleBatchApprove}
                        disabled={selectedRules.length === 0 || batchApproving}
                      >
                        {batchApproving ? 'Approving...' : 'Approve Selected'}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* History List */}
            {history.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No Documents Processed
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Upload policy documents to automatically generate security rules
                </Typography>
              </Box>
            ) : (
              <Box>
                {history?.map((item, index) => (
                  <Accordion key={index} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box display="flex" alignItems="center" width="100%" gap={2}>
                        <Description color="primary" />
                        <Box flexGrow={1}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {item.document_name}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <Chip 
                              label={getDocumentTypeLabel(item.document_type)} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                            <Typography variant="caption" color="textSecondary">
                              Processed {formatDate(item.processing_timestamp)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              by {item.creator.first_name} {item.creator.last_name}
                            </Typography>
                          </Box>
                        </Box>
                        <Box textAlign="center">
                          <Typography variant="h6" color="primary">
                            {item.statistics.total_rules}
                          </Typography>
                          <Typography variant="caption">
                            Rules Generated
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    
                    <AccordionDetails>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={3}>
                          <Card>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                              <Typography variant="h4" color="success.main">
                                {item.statistics.approved_rules}
                              </Typography>
                              <Typography variant="caption">
                                Approved
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={3}>
                          <Card>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                              <Typography variant="h4" color="warning.main">
                                {item.statistics.pending_rules}
                              </Typography>
                              <Typography variant="caption">
                                Pending
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={3}>
                          <Card>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                              <Typography variant="h4" color="info.main">
                                {item.statistics.active_rules}
                              </Typography>
                              <Typography variant="caption">
                                Active
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={3}>
                          <Card>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                              <Typography variant="h4">
                                {item.statistics.total_rules}
                              </Typography>
                              <Typography variant="caption">
                                Total
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      <Typography variant="subtitle2" gutterBottom>
                        Generated Rules:
                      </Typography>
                      <List dense>
                        {item.rules?.map((rule, ruleIndex) => (
                          <ListItem key={ruleIndex}>
                            <ListItemIcon>
                              <Security fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={rule.name}
                              secondary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Chip
                                    label={rule.approval_status}
                                    size="small"
                                    color={getStatusColor(rule.approval_status) as any}
                                    variant="outlined"
                                  />
                                  {rule.is_active && (
                                    <Chip
                                      label="Active"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                    />
                                  )}
                                  <Typography variant="caption" color="textSecondary">
                                    Created {formatDate(rule.created_at)}
                                  </Typography>
                                </Box>
                              }
                            />
                            {rule.approval_status === 'pending' && (
                              <ListItemSecondaryAction>
                                <Button
                                  size="small"
                                  variant={selectedRules.includes(rule.id) ? "contained" : "outlined"}
                                  onClick={() => toggleRuleSelection(rule.id)}
                                >
                                  {selectedRules.includes(rule.id) ? 'Selected' : 'Select'}
                                </Button>
                              </ListItemSecondaryAction>
                            )}
                          </ListItem>
                        )) || []}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )) || []}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
        {selectedRules.length > 0 && (
          <Button
            variant="contained"
            onClick={handleBatchApprove}
            disabled={batchApproving}
          >
            {batchApproving ? 'Approving...' : `Approve ${selectedRules.length} Rules`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DocumentProcessingHistory;
